import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Spinner, Stack, Text, XStack } from "tamagui";
import { RiBankCardLine } from "react-icons/ri";
import { OwnedPaymentMethod } from "@screen/Home/pages/Account/API/handlePaymentAPI";
import type { PaymentSelection, WalletBrand } from "@utils/store/cart/useOrder";
import InlineBindCardForm, { BoundCard } from "./InlineBindCardForm";
import { WALLET_BRANDS, catFromSelection, readLastPayCat, readLastPayment, type PayCatId } from "../utils/paymentSelection";
import { TAPPAY_LOGO_URL, TAPPAY_SECURITY_COPY_LEAD, TAPPAY_SECURITY_COPY_REST } from "@components/payment/TapPayCompliance";
import "../gasPayment.scss";

const applePayLogo = require("@styles/images/payment/apple_pay.png") as string;
const linePayLogo = require("@styles/images/payment/line_pay.png") as string;
const ipassMoneyLogo = require("@styles/images/payment/ipass_money.png") as string;
const cashBillImg = require("@styles/images/payment/cash_ntd100.png") as string;

const WALLET_LOGOS: Record<WalletBrand, string> = {
  apple_pay: applePayLogo,
  line_pay: linePayLogo,
  ipass_money: ipassMoneyLogo,
};

/** 一卡通 MONEY 授權狀態（選了 iPASS 才查）：查詢中／已授權／尚未授權 */
export type IPassAuthState = "checking" | "authorized" | "unauthorized";

const TABS: PayCatId[] = ["card", "wallet", "cash"];

/** 白底列表上的卡別徽章 */
const Badge = ({ children }: { children: ReactNode }) => <span className="gas-pay-badge">{children}</span>;

/** 依 bincode 首碼判斷卡別（4=Visa、5/2=Mastercard、3=JCB，其餘泛用卡）。 */
const CardBrand = ({ bincode }: { bincode?: string }) => {
  const d = (bincode || "")[0];
  if (d === "4")
    return (
      <Badge>
        <Text fontSize={13} fontWeight="800" fontStyle="italic" color="#1a1f71" letterSpacing={-0.3}>
          VISA
        </Text>
      </Badge>
    );
  if (d === "5" || d === "2")
    return (
      <Badge>
        <XStack ai="center">
          <Stack width={13} height={13} borderRadius={7} backgroundColor="#eb001b" />
          <Stack width={13} height={13} borderRadius={7} backgroundColor="#f79e1b" ml={-6} opacity={0.92} />
        </XStack>
      </Badge>
    );
  if (d === "3")
    return (
      <Badge>
        <Text fontSize={11} fontWeight="900" color="#0b6cb3" letterSpacing={-0.2}>
          JCB
        </Text>
      </Badge>
    );
  return (
    <Badge>
      <RiBankCardLine size={19} color="#8a93a6" />
    </Badge>
  );
};

/** 一列可選的付款方式（單選）。 */
const OptionRow = ({
  leading,
  title,
  sub,
  picked,
  lastUsed,
  onPress,
}: {
  leading: ReactNode;
  title: string;
  sub: string;
  picked: boolean;
  lastUsed?: boolean;
  onPress: () => void;
}) => (
  <button type="button" role="radio" aria-checked={picked} className={`gas-pay-row${picked ? " picked" : ""}`} onClick={onPress}>
    {leading}
    <span className="gas-pay-row-text">
      <span className="gas-pay-row-title">
        {title}
        {lastUsed ? <span className="gas-pay-last">上次使用</span> : null}
      </span>
      <span className="gas-pay-row-sub">{sub}</span>
    </span>
    <span className="gas-pay-radio" aria-hidden />
  </button>
);

/** 分類小圖：信用卡（晶片）／手機是純 CSS 靜態圖形，現金用百元鈔插圖。 */
const TileArt = ({ kind }: { kind: PayCatId }) =>
  kind === "cash" ? (
    <img className="gas-pay-art a-cash" src={cashBillImg} alt="" aria-hidden draggable={false} />
  ) : (
    <span className={`gas-pay-art a-${kind}`} aria-hidden>
      <i />
    </span>
  );

/**
 * 瓦斯訂購「付款方式」步驟：上方三個分類（信用卡／電子支付／現金或月結），下方是該類的選項。
 * 進頁預設沿用上次的付款方式（父層依 pickDefaultPayment 預選），分類跟著選擇打開。
 * 只負責選擇與綁卡，實際扣款由後端於送達後處理。
 */
const GasPaymentOptions = ({
  isMonthly,
  selected,
  onSelect,
  onBound,
  cusJwtToken,
  cards,
  loading,
  showApplePay,
  ipassAuth,
  onAuthorizeIPass,
}: {
  isMonthly: boolean;
  selected: PaymentSelection | null;
  onSelect: (v: PaymentSelection | null) => void;
  /** 綁卡成功（內嵌表單）；父層負責 reload／選中。 */
  onBound: (created?: BoundCard) => void;
  cusJwtToken: string;
  cards: OwnedPaymentMethod[];
  loading: boolean;
  /** iPhone 才提供 Apple Pay；LINE Pay 一律提供。 */
  showApplePay: boolean;
  /** 選了一卡通 MONEY 時的授權狀態（父層查）；沒傳就不顯示授權區 */
  ipassAuth?: IPassAuthState;
  /** 導去一卡通授權（離站，授權完會回到付款方式頁） */
  onAuthorizeIPass?: () => void;
}) => {
  const cashTitle = isMonthly ? "月結" : "現金";
  const cashSub = isMonthly ? "月底結算" : "當面付現";
  const cashSelection: PaymentSelection = isMonthly ? { type: "monthly" } : { type: "cash_on_delivery" };
  const brands = WALLET_BRANDS.filter((b) => b.id !== "apple_pay" || showApplePay);
  const selCat = catFromSelection(selected);
  const curCardId = selected?.type === "credit_card" ? selected.paymentMethodId : "";
  const curBrand = selected?.type === "electronic" ? selected.brand : null;
  const ipassPicked = curBrand === "ipass_money";

  /** 「上次使用」標籤：進頁時讀一次，當次切換不跟著變。 */
  const lastUsed = useMemo(() => readLastPayment(), []);
  const [tab, setTab] = useState<PayCatId>(() => selCat ?? readLastPayCat());
  const [bindOpen, setBindOpen] = useState(false);
  const [bindMounted, setBindMounted] = useState(false);

  /** 每一類最後選的項目：切去別類看一眼再切回來，不用重選。 */
  const perTab = useRef<Partial<Record<PayCatId, PaymentSelection>>>({});
  useEffect(() => {
    if (selected && selCat) perTab.current[selCat] = selected;
  }, [selected, selCat]);

  // 父層非同步預選（卡片載完才帶上次的卡）時，分類跟著跳過去
  useEffect(() => {
    // 用函式型更新，只在選擇變動時同步，不必把 tab 放進依賴
    if (selCat) setTab((t) => (t === selCat ? t : selCat));
  }, [selCat]);

  const openTab = (id: PayCatId) => {
    if (id === tab) return;
    setTab(id);
    setBindOpen(false);
    if (id === "cash") onSelect(cashSelection);
    else onSelect(perTab.current[id] ?? null);
  };

  const panelTitle = tab === "card" ? "選擇信用卡" : tab === "wallet" ? "選擇電子支付" : isMonthly ? "月結付款" : "現金付款";

  return (
    <div className="gas-pay">
      <div className="gas-pay-tiles" role="tablist" aria-label="付款類別">
        {TABS.map((id) => {
          const on = tab === id;
          const title = id === "card" ? "信用卡" : id === "wallet" ? "電子支付" : cashTitle;
          const sub = id === "card" ? "送達後扣款" : id === "wallet" ? "掃碼／自動扣" : cashSub;
          return (
            <button key={id} type="button" role="tab" aria-selected={on} className={`gas-pay-tile${on ? " on" : ""}`} onClick={() => openTab(id)}>
              <TileArt kind={id} />
              <span className="gas-pay-tile-title">{title}</span>
              <span className="gas-pay-tile-sub">{sub}</span>
            </button>
          );
        })}
      </div>

      <div className="gas-pay-panel" role="tabpanel">
        <div className="gas-pay-panel-title">{panelTitle}</div>

        {tab === "card" &&
          (loading ? (
            <XStack ai="center" gap={8} px={10} py={12}>
              <Spinner size="small" color="#2196f3" />
              <Text fontSize={13} color="#64748B">
                載入付款方式…
              </Text>
            </XStack>
          ) : (
            <div role="radiogroup" aria-label="信用卡" className="gas-pay-rows">
              {!bindOpen &&
                cards.map((c) => (
                  <OptionRow
                    key={c.payment_method_id}
                    leading={<CardBrand bincode={c.card?.bincode} />}
                    title={`•••• ${c.card?.lastfour ?? "----"}`}
                    sub={c.card?.issuer || c.nickname || "信用卡"}
                    picked={curCardId === c.payment_method_id}
                    lastUsed={lastUsed?.type === "credit_card" && lastUsed.paymentMethodId === c.payment_method_id}
                    onPress={() => onSelect({ type: "credit_card", paymentMethodId: c.payment_method_id, label: `信用卡 •••• ${c.card?.lastfour ?? "----"}` })}
                  />
                ))}
              {!bindOpen && (
                <button
                  type="button"
                  className="gas-pay-add"
                  onClick={() => {
                    setBindMounted(true);
                    setBindOpen(true);
                  }}
                >
                  {cards.length === 0 ? "綁定信用卡" : "＋ 新增信用卡"}
                </button>
              )}
              {!bindOpen && <p className="gas-pay-note">瓦斯送到後才扣款，可隨時在訂單中改付款方式。</p>}
              {/* 綁卡表單沿用深色卡面的樣式，包一塊卡片色的底 */}
              {bindMounted && (
                <div className={`gas-pay-bindbox${bindOpen ? " open" : ""}`}>
                  <InlineBindCardForm
                    open={bindOpen}
                    cusJwtToken={cusJwtToken}
                    onCancel={() => setBindOpen(false)}
                    onBound={(created) => {
                      setBindOpen(false);
                      onBound(created);
                    }}
                  />
                </div>
              )}
            </div>
          ))}

        {tab === "wallet" && (
          <div role="radiogroup" aria-label="電子支付" className="gas-pay-rows">
            {brands.map((b) => (
              <OptionRow
                key={b.id}
                leading={
                  <span className="gas-pay-badge logo">
                    <img src={WALLET_LOGOS[b.id]} alt="" draggable={false} />
                  </span>
                }
                title={b.label}
                sub={b.id === "ipass_money" ? "簽收後自動扣款，免掃碼" : "送達時掃配送員的付款碼"}
                picked={curBrand === b.id}
                lastUsed={lastUsed?.type === "electronic" && lastUsed.brand === b.id}
                onPress={() => onSelect({ type: "electronic", brand: b.id, label: b.label })}
              />
            ))}
            {ipassPicked && ipassAuth === "authorized" && <div className="gas-pay-ok">✓ 已授權一卡通 MONEY 自動扣款</div>}
            {ipassPicked && ipassAuth === "unauthorized" && (
              <div className="gas-pay-auth">
                <p>第一次使用要先到一卡通授權，授權完成後會回到這一頁，再按「前往確認訂單」。</p>
                <button type="button" onClick={() => onAuthorizeIPass?.()}>
                  前往一卡通授權
                </button>
              </div>
            )}
            {ipassPicked && ipassAuth === "checking" && (
              <XStack ai="center" gap={8} px={10} py={8}>
                <Spinner size="small" color="#F08C00" />
                <Text fontSize={13} color="#64748B">
                  確認一卡通授權中…
                </Text>
              </XStack>
            )}
            {/* TapPay 揭露（審核要求）只適用 TapPay 收的錢包；一卡通 MONEY 不經 TapPay */}
            {!ipassPicked && (
              <p className="gas-pay-security">
                {TAPPAY_SECURITY_COPY_LEAD}
                <img src={TAPPAY_LOGO_URL} alt="TapPay" />
                {TAPPAY_SECURITY_COPY_REST}
              </p>
            )}
          </div>
        )}

        {tab === "cash" && (
          <div role="radiogroup" aria-label={cashTitle} className="gas-pay-rows">
            <OptionRow
              leading={<TileArt kind="cash" />}
              title={isMonthly ? "月結" : "貨到付款"}
              sub={isMonthly ? "本月訂單彙總後再結算" : "送達時付現給配送員"}
              picked={selCat === "cash"}
              lastUsed={lastUsed?.type === "cash_on_delivery" || lastUsed?.type === "monthly"}
              onPress={() => onSelect(cashSelection)}
            />
            <p className="gas-pay-note">{isMonthly ? "無需當場付款，月底統一結算。" : "建議備妥零錢，方便配送員找零。"}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GasPaymentOptions;
